import { BaseSocialLogin } from './base';
import { createUserCancelledError } from './errors';
export class FacebookSocialLogin extends BaseSocialLogin {
    constructor() {
        super(...arguments);
        this.appId = null;
        this.scriptLoaded = false;
        this.locale = 'en_US';
    }
    async initialize(appId, locale) {
        this.appId = appId;
        if (locale) {
            this.locale = locale;
        }
        if (appId) {
            // Load with the specified locale or default
            await this.loadFacebookScript(this.locale);
            FB.init({
                appId: this.appId,
                version: 'v17.0',
                xfbml: true,
                cookie: true,
            });
        }
    }
    async login(options) {
        if (!this.appId) {
            throw new Error('Facebook App ID not set. Call initialize() first.');
        }
        return new Promise((resolve, reject) => {
            var _a;
            let settled = false;
            let waitingForStatus = false;
            const resolveWithProfile = (authResponse) => {
                if (settled)
                    return;
                if (!(authResponse === null || authResponse === void 0 ? void 0 : authResponse.accessToken) || !authResponse.userID) {
                    settled = true;
                    reject(new Error('Facebook login failed'));
                    return;
                }
                const accessToken = authResponse.accessToken;
                const userId = authResponse.userID;
                FB.api('/me', { fields: 'id,name,email,picture' }, (userInfo) => {
                    var _a, _b;
                    settled = true;
                    const result = {
                        accessToken: {
                            token: accessToken,
                            userId,
                        },
                        profile: {
                            userID: userInfo.id,
                            name: userInfo.name,
                            email: userInfo.email || null,
                            imageURL: ((_b = (_a = userInfo.picture) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.url) || null,
                            friendIDs: [],
                            birthday: null,
                            ageRange: null,
                            gender: null,
                            location: null,
                            hometown: null,
                            profileURL: null,
                        },
                        idToken: null,
                    };
                    resolve({ provider: 'facebook', result });
                });
            };
            const waitForConnected = () => {
                if (settled || waitingForStatus)
                    return;
                waitingForStatus = true;
                this.waitForConnection()
                    .then((statusResponse) => resolveWithProfile(statusResponse.authResponse))
                    .catch((error) => {
                    if (settled)
                        return;
                    settled = true;
                    reject(error);
                });
            };
            FB.login((response) => {
                if (response.status === 'connected') {
                    resolveWithProfile(response.authResponse);
                }
                else if (response.status === 'not_authorized' || response.status === 'unknown') {
                    reject(createUserCancelledError('Facebook login was cancelled.'));
                }
                else {
                    waitForConnected();
                }
            }, ((_a = options.permissions) === null || _a === void 0 ? void 0 : _a.length) ? { scope: options.permissions.join(',') } : undefined);
        });
    }
    async logout() {
        return new Promise((resolve) => {
            FB.logout(() => resolve());
        });
    }
    async isLoggedIn() {
        return new Promise((resolve) => {
            FB.getLoginStatus((response) => {
                resolve({ isLoggedIn: response.status === 'connected' });
            });
        });
    }
    async getAuthorizationCode() {
        return new Promise((resolve, reject) => {
            FB.getLoginStatus((response) => {
                var _a;
                if (response.status === 'connected') {
                    resolve({ accessToken: ((_a = response.authResponse) === null || _a === void 0 ? void 0 : _a.accessToken) || '' });
                }
                else {
                    reject(new Error('No Facebook authorization code available'));
                }
            });
        });
    }
    async refresh(options) {
        await this.login(options);
    }
    async getProfile(fields) {
        if (!this.appId) {
            throw new Error('Facebook App ID not set. Call initialize() first.');
        }
        const requestedFields = Array.isArray(fields) && fields.length > 0 ? fields : ['id', 'name', 'email', 'picture'];
        return new Promise((resolve, reject) => {
            FB.getLoginStatus((statusResponse) => {
                if (statusResponse.status !== 'connected') {
                    reject(new Error('User is not logged in. Call login() before fetching profile.'));
                    return;
                }
                FB.api('/me', { fields: requestedFields.join(',') }, (profile) => {
                    var _a, _b, _c, _d, _e, _f, _g;
                    if (profile && !profile.error) {
                        resolve({
                            profile: Object.assign(Object.assign({}, profile), { id: (_a = profile.id) !== null && _a !== void 0 ? _a : null, name: (_b = profile.name) !== null && _b !== void 0 ? _b : null, email: (_c = profile.email) !== null && _c !== void 0 ? _c : null, first_name: (_d = profile.first_name) !== null && _d !== void 0 ? _d : null, last_name: (_e = profile.last_name) !== null && _e !== void 0 ? _e : null }),
                        });
                    }
                    else {
                        const errorMessage = (_g = (_f = profile === null || profile === void 0 ? void 0 : profile.error) === null || _f === void 0 ? void 0 : _f.message) !== null && _g !== void 0 ? _g : 'Failed to fetch Facebook profile';
                        reject(new Error(errorMessage));
                    }
                });
            });
        });
    }
    async requestTracking() {
        if (!this.appId) {
            throw new Error('Facebook App ID not set. Call initialize() first.');
        }
        // App Tracking Transparency is not applicable on web.
        return { status: 'authorized' };
    }
    waitForConnection(timeoutMs = 120000, pollIntervalMs = 500) {
        const start = Date.now();
        return new Promise((resolve, reject) => {
            let finished = false;
            const pollStatus = () => {
                if (finished)
                    return;
                FB.getLoginStatus((response) => {
                    var _a;
                    if (finished)
                        return;
                    if (response.status === 'connected' && ((_a = response.authResponse) === null || _a === void 0 ? void 0 : _a.accessToken)) {
                        finished = true;
                        resolve(response);
                        return;
                    }
                    if (response.status === 'not_authorized' || response.status === 'unknown') {
                        finished = true;
                        reject(createUserCancelledError('Facebook login was cancelled.'));
                        return;
                    }
                    if (Date.now() - start >= timeoutMs) {
                        finished = true;
                        reject(new Error('Facebook login failed or timed out'));
                        return;
                    }
                    setTimeout(pollStatus, pollIntervalMs);
                });
            };
            pollStatus();
        });
    }
    async loadFacebookScript(locale) {
        if (this.scriptLoaded)
            return;
        // Remove any existing Facebook SDK script
        const existingScript = document.querySelector('script[src*="connect.facebook.net"]');
        if (existingScript) {
            existingScript.remove();
        }
        return this.loadScript(`https://connect.facebook.net/${locale}/sdk.js`).then(() => {
            this.scriptLoaded = true;
        });
    }
}
//# sourceMappingURL=facebook-provider.js.map